/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
 * The base colors for the Material Dashboard 2 React.
 * You can add new color using this file.
 * You can customized the colors for the entire Material Dashboard 2 React using thie file.
 *
 * Renix redesign (Phase 1): neutral operational values retargeted here; preset machinery
 * (`info` + `gradients.info` + `brand`) is injected by buildLightTheme and left untouched.
 */

const colors = {
  background: {
    default: "#F4F7FA",
  },

  text: {
    main: "#172033",
    focus: "#5F6B7A",
  },

  transparent: {
    main: "transparent",
  },

  white: {
    main: "#ffffff",
    focus: "#ffffff",
  },

  black: {
    light: "#000000",
    main: "#000000",
    focus: "#000000",
  },

  primary: {
    main: "#1769AA",
    focus: "#125582",
  },

  secondary: {
    main: "#7b809a",
    focus: "#8f93a9",
  },

  info: {
    main: "#0072D4",
    focus: "#005EB3",
  },

  success: {
    main: "#1E7F4F",
    focus: "#16603C",
  },

  brand: {
    primary: "#0072D4",
    primaryFocus: "#005EB3",
    secondary: "#F56242",
    secondaryFocus: "#D94E33",
  },

  warning: {
    // #B26A00 measured 4.24:1 on white (and 3.84:1 on its own 8% StatusBadge tint) — under AA.
    main: "#A05F00",
    focus: "#8F5600",
  },

  error: {
    main: "#C0392B",
    focus: "#A93226",
  },

  light: {
    main: "#F4F7FA",
    focus: "#E8EDF3",
  },

  dark: {
    main: "#172033",
    focus: "#10151D",
  },

  grey: {
    100: "#f8f9fa",
    200: "#f0f2f5",
    300: "#dee2e6",
    400: "#ced4da",
    500: "#adb5bd",
    600: "#6c757d",
    700: "#495057",
    800: "#343a40",
    900: "#212529",
  },

  gradients: {
    primary: {
      main: "#EC407A",
      state: "#D81B60",
    },

    secondary: {
      main: "#747b8a",
      state: "#495361",
    },

    info: {
      main: "#339AEB",
      state: "#0072D4",
    },

    success: {
      main: "#4ade80",
      state: "#16a34a",
    },

    warning: {
      main: "#FFA726",
      state: "#FB8C00",
    },

    error: {
      main: "#EF5350",
      state: "#E53935",
    },

    light: {
      main: "#EBEFF4",
      state: "#CED4DA",
    },

    dark: {
      main: "#42424a",
      state: "#191919",
    },
  },

  socialMediaColors: {
    facebook: {
      main: "#3b5998",
      dark: "#344e86",
    },

    twitter: {
      main: "#55acee",
      dark: "#3ea1ec",
    },

    instagram: {
      main: "#125688",
      dark: "#0e456d",
    },

    linkedin: {
      main: "#0077b5",
      dark: "#00669c",
    },

    pinterest: {
      main: "#cc2127",
      dark: "#b21d22",
    },

    youtube: {
      main: "#e52d27",
      dark: "#d41f1a",
    },

    vimeo: {
      main: "#1ab7ea",
      dark: "#13a3d2",
    },

    slack: {
      main: "#3aaf85",
      dark: "#329874",
    },

    dribbble: {
      main: "#ea4c89",
      dark: "#e73177",
    },

    github: {
      main: "#24292e",
      dark: "#171a1d",
    },

    reddit: {
      main: "#ff4500",
      dark: "#e03d00",
    },

    tumblr: {
      main: "#35465c",
      dark: "#2a3749",
    },
  },

  badgeColors: {
    primary: {
      background: "#f8b3ca",
      text: "#cc084b",
    },

    secondary: {
      background: "#d7d9e1",
      text: "#6c757d",
    },

    info: {
      background: "#CCE3F7",
      text: "#004A8A",
    },

    success: {
      background: "#dcfce7",
      text: "#14532d",
    },

    warning: {
      background: "#ffd59f",
      text: "#c87000",
    },

    error: {
      background: "#fcd3d0",
      text: "#f61200",
    },

    light: {
      background: "#ffffff",
      text: "#c7d3de",
    },

    dark: {
      background: "#8097bf",
      text: "#1e2e4a",
    },
  },

  coloredShadows: {
    primary: "#e91e62",
    secondary: "#110e0e",
    info: "rgba(0, 114, 212, 0.4)",
    success: "rgba(22, 163, 74, 0.4)",
    warning: "#ff9900",
    error: "#f44336",
    light: "#adb5bd",
    dark: "#404040",
  },

  inputBorderColor: "#d2d6da",

  // ===== Renix redesign tokens (additive — see docs/redesign_plan.md §6.B.1) =====
  // Preset-independent neutrals for the "operations ledger" look. Consumed by
  // components/ui primitives and (from Phase 1) by restyled component overrides.
  canvas: { main: "#F4F7FA" },
  surface: { main: "#FFFFFF" },
  ink: { main: "#172033", secondary: "#5F6B7A" },
  // `main` is the decorative hairline (table rows, card edges — not a 1.4.11 target).
  // `control` is the resting outline of form controls, which 1.4.11 does require to hold
  // 3:1 against the surface; `strong` is its hover step.
  borderColor: { main: "#DCE3EA", control: "#8D959E", strong: "#6B7683" },
  // `onSurface` is the text/link shade (see functions/accessibleAccent). These static values
  // are the no-preset fallback; buildLightTheme replaces all three per active preset.
  accent: {
    main: "#1769AA",
    focus: "#125582",
    soft: "rgba(23,105,170,0.08)",
    onSurface: "#1769AA",
  },

  tabs: {
    indicator: { boxShadow: "#ddd" },
  },
};

export default colors;
